import { inject } from '@angular/core'
import { toObservable } from '@angular/core/rxjs-interop'
import { ResolveFn } from '@angular/router'

import { Observable, map } from 'rxjs'

import { DetailedPermissions } from '../shared/workspace.utils'
import { WorkspacesAndPermissionsStore } from '../workspaces-and-permissions.store'

export const OrgAdminFontsPermissionsResolver: ResolveFn<
  DetailedPermissions
> = (): Observable<DetailedPermissions> => {
  const workspacesAndPermissionsStore = inject(WorkspacesAndPermissionsStore)
  return toObservable(workspacesAndPermissionsStore.userAndWorkspacePermissions).pipe(
    map((permissions) => ({
      canCreate: !!permissions['org_font_create'],
      canEdit: !!permissions['org_font_edit'],
      canDelete: !!permissions['org_font_delete'],
      canImport: !!permissions['org_font_edit'],
      canExport: !!permissions['org_font_read'],
    }))
  )
}

export const OrgAdminUsersPermissionsResolver: ResolveFn<
  DetailedPermissions
> = (): Observable<DetailedPermissions> => {
  const workspacesAndPermissionsStore = inject(WorkspacesAndPermissionsStore)
  return toObservable(workspacesAndPermissionsStore.userAndWorkspacePermissions).pipe(
    map((permissions) => ({
      canCreate: !!permissions['org_users_create'] || !!permissions.org_runtime_users_create,
      canEdit: !!permissions['org_users_edit'] || !!permissions.org_runtime_users_edit,
      canDelete: !!permissions['org_users_delete'] || !!permissions.org_runtime_users_delete,
      canChangePassword: !!permissions['org_users_edit'] || !!permissions.org_runtime_users_edit,
    }))
  )
}

export const OrgAdminGroupsPermissionsResolver: ResolveFn<
  DetailedPermissions
> = (): Observable<DetailedPermissions> => {
  const workspacesAndPermissionsStore = inject(WorkspacesAndPermissionsStore)
  return toObservable(workspacesAndPermissionsStore.userAndWorkspacePermissions).pipe(
    map((permissions) => ({
      canCreate: !!permissions['org_groups_create'] || !!permissions.org_runtime_groups_create,
      canEdit: !!permissions['org_groups_edit'] || !!permissions.org_runtime_groups_edit,
      canDelete: !!permissions['org_groups_delete'] || !!permissions.org_runtime_groups_delete,
    }))
  )
}

export const OrgAdminRolesPermissionsResolver: ResolveFn<
  DetailedPermissions
> = (): Observable<DetailedPermissions> => {
  const workspacesAndPermissionsStore = inject(WorkspacesAndPermissionsStore)
  return toObservable(workspacesAndPermissionsStore.userAndWorkspacePermissions).pipe(
    map((permissions) => ({
      canCreate: !!permissions['org_roles_create'] || !!permissions.org_runtime_roles_create,
      canEdit: !!permissions['org_roles_edit'] || !!permissions.org_runtime_roles_edit,
      canDelete: !!permissions['org_roles_delete'] || !!permissions.org_runtime_roles_delete,
    }))
  )
}

export const OrgAdminOutOfOfficePermissionsResolver: ResolveFn<
  DetailedPermissions
> = (): Observable<DetailedPermissions> => {
  const workspacesAndPermissionsStore = inject(WorkspacesAndPermissionsStore)

  return toObservable(workspacesAndPermissionsStore.userAndWorkspacePermissions).pipe(
    map((permissions) => ({
      canCreate: !!permissions['org_ooo_create'],
      canEdit: !!permissions['org_ooo_edit'],
      canDelete: !!permissions['org_ooo_delete'],
    }))
  )
}

export const OrgAdminRuntimeProjectDetailsPermissionsResolver: ResolveFn<
  DetailedPermissions
> = (): Observable<DetailedPermissions> => {
  const workspacesAndPermissionsStore = inject(WorkspacesAndPermissionsStore)

  return toObservable(workspacesAndPermissionsStore.userAndWorkspacePermissions).pipe(
    map((permissions) => {
      const canManageLinks = !!(
        permissions.org_runtime_users_create &&
        permissions.org_runtime_projects_read &&
        permissions.org_runtime_roles_edit
      )

      return {
        canRead: !!(permissions.org_runtime_users_read && permissions.org_runtime_projects_read),
        canCreate: canManageLinks,
        canEdit: canManageLinks,
      }
    })
  )
}
